/* eslint-disable sort-imports */
import { ComboBox } from 'src/components/ui/combobox';
import FooterPrivacy from '../../../auth/components/baseTemplate/FooterPrivacy';
import Prompt from 'src/components/ui/NavigationPrompt';
import DialogModal from 'src/components/ui/dialogModal';
import Dropdown from 'src/components/ui/dropdownMenuTable/dropdownMenuTable';
import {
    DealerManagementService,
    ISaveDistributionRulePayload,
} from 'src/services/dealer-management.service';
import DistributionMultiSelectComboBox, {
    IDistributionMultiSelectOption,
} from './components/distribution-multiselect-combobox';
import { RadioButton } from 'src/components/ui/radioButton';
import { getStateOptions } from 'src/components/credit/application/utils';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import ProfileOptionsIcon from 'src/components/dealer-mgmt/profile-options-icon';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IUser } from 'src/utils/interfaces/user';
import { Brands } from 'src/utils/enums/brands.enum';
import { useLocalization } from 'src/utils/useLocalization';
import {
    DistributionRuleService,
    IDistributionRuleDetails,
} from 'src/services/distribution-rule.service';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';

import './styles.scss';

const RULE_SCOPES = {
    REGION_DISTRICT: 'region-district',
    STATE_PROVINCE: 'state-province',
    DEALERSHIP: 'dealership',
} as const;

type RuleScope = (typeof RULE_SCOPES)[keyof typeof RULE_SCOPES];

const COUNTRIES = {
    USA: 'US',
    CANADA: 'CA',
} as const;

const BRANDS = {
    VW: Brands.VW,
    AUDI: Brands.AUDI,
    DUCATI: Brands.DUCATI,
} as const;

interface IValueLabelOption {
    value: string;
    label: string;
}

interface IRegionOption {
    orgUnitId: number | string;
    regionName: string;
    regionCode?: number | string;
}

interface IDistrictOption {
    districtName: string;
    districtId?: number | string;
}

interface IStateProvinceOption {
    value: string;
    label: string;
}

interface IStateProvinceDropdownOption extends IStateProvinceOption {
    code: string;
}

interface IDealerOption {
    dealerId: string;
    dealerName?: string;
}

const EMPTY_OPTIONS = 0;
const SINGLE_SELECTION = 1;
const RULE_NAME_MAX_LENGTH = 50;
const USA_STATE_PREFIX = '1_';
const CANADA_PROVINCE_PREFIX = '2_';
const RADIO_OPTIONS: RuleScope[] = [
    RULE_SCOPES.REGION_DISTRICT,
    RULE_SCOPES.STATE_PROVINCE,
    RULE_SCOPES.DEALERSHIP,
];

const normalizeRegions = (data: any): IRegionOption[] => {
    const rawRegions = data?.regions?.region ?? data?.regions ?? data;
    const regionList: Array<any> = Array.isArray(rawRegions)
        ? rawRegions
        : Array.isArray(rawRegions?.region)
        ? rawRegions.region
        : [];

    return regionList
        .filter((region: any) => !!region?.orgUnitId && !!region?.regionName)
        .map((region: any) => ({
            orgUnitId: region.orgUnitId,
            regionName: region.regionName,
            regionCode: region.regionCode,
        }));
};

const normalizeDistricts = (data: any): IDistrictOption[] => {
    const rawDistricts = data?.districts?.district ?? data?.districts ?? data;
    const districtList: Array<any> = Array.isArray(rawDistricts)
        ? rawDistricts
        : Array.isArray(rawDistricts?.district)
        ? rawDistricts.district
        : [];

    return districtList
        .filter((district: any) => !!district?.districtName)
        .map((district: any) => ({
            districtName: district.districtName,
            districtId: district.districtId ?? district.districtCode,
        }));
};

const normalizeDealersByState = (data: any): IDealerOption[] => {
    const rawDealers =
        data?.dealerIds?.dealerId ?? data?.dealerIds ?? data?.dealers ?? data;
    const dealerList: Array<any> = Array.isArray(rawDealers)
        ? rawDealers
        : Array.isArray(rawDealers?.dealer)
        ? rawDealers.dealer
        : [];

    return (
        dealerList
            .map((dealer) => {
                if (typeof dealer === 'string' || typeof dealer === 'number') {
                    return { dealerId: String(dealer) };
                }

                const dealerId =
                    dealer?.dealerId ??
                    dealer?.dealerCode ??
                    dealer?.id ??
                    dealer?.value;

                if (!dealerId) {
                    return null;
                }

                return {
                    dealerId: String(dealerId),
                    dealerName: dealer?.dealerName ?? dealer?.name,
                };
            })
            .filter(Boolean) as IDealerOption[]
    ).sort((dealerA, dealerB) =>
        dealerA.dealerId.localeCompare(dealerB.dealerId, undefined, {
            numeric: true,
        }),
    );
};

const getDealerDisplayLabel = (dealer: IDealerOption): string => {
    return dealer.dealerName
        ? `${dealer.dealerId} - ${dealer.dealerName}`
        : dealer.dealerId;
};

const getStateProvinceDisplayLabel = (
    option: IStateProvinceDropdownOption,
): string => {
    return option.label === option.code
        ? option.code
        : `${option.code} - ${option.label}`;
};

const toStringValues = (values?: Array<string | number>): string[] => {
    return (values ?? [])
        .filter(
            (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value));
};

// state values arrive prefixed by country, e.g. '1_CT'
const getStateProvinceCodeFromValue = (value: string): string => {
    const [, code] = value.split('_');
    return code || value;
};

const RuleFormSkeleton: React.FC = () => (
    <div
        className='c-form-section__content create-distribution-rule__content create-distribution-rule__skeleton'
        role='status'
        aria-busy='true'
        data-testid='distribution-rule-skeleton'
    >
        <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--title' />
        <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--description' />

        <div className='create-distribution-rule__rule-name-field mb-large'>
            <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--label' />
            <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--input' />
        </div>

        <div className='create-distribution-rule__top-select-row mb-large'>
            <div className='create-distribution-rule__selector-field'>
                <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--label' />
                <div className='create-distribution-rule__dropdown-skeleton' />
            </div>
            <div className='create-distribution-rule__selector-field'>
                <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--label' />
                <div className='create-distribution-rule__dropdown-skeleton' />
            </div>
        </div>

        <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--section-title' />
        <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--description' />

        <div className='create-distribution-rule__type-container'>
            <div className='create-distribution-rule__type-options'>
                <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--radio' />
                <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--radio' />
                <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--radio' />
            </div>

            <div className='create-distribution-rule__divider' />

            <div className='create-distribution-rule__selectors'>
                <div className='create-distribution-rule__selector-field'>
                    <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--label' />
                    <div className='create-distribution-rule__dropdown-skeleton' />
                </div>
                <div className='create-distribution-rule__selector-field'>
                    <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--label' />
                    <div className='create-distribution-rule__dropdown-skeleton' />
                </div>
            </div>
        </div>

        <div className='create-distribution-rule__actions'>
            <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--button' />
            <div className='create-distribution-rule__skeleton-line create-distribution-rule__skeleton-line--button' />
        </div>
    </div>
);

const CreateDistributionRule: React.FC = () => {
    const navigate = useNavigate();
    const { state: routeState } = useLocation();
    const distributionId =
        (routeState as { distributionId?: string } | null)?.distributionId ??
        '';
    const { systemAdmin, occupationIndividualInformation, errorMessages } =
        useLocalization();
    const brand: string = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer.brand || 'vw';
    });
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedBrand, setSelectedBrand] = useState<string>('');
    const [selectedScope, setSelectedScope] = useState<RuleScope>(
        RULE_SCOPES.REGION_DISTRICT,
    );
    const [regions, setRegions] = useState<IRegionOption[]>([]);
    const [districts, setDistricts] = useState<IDistrictOption[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
    const [selectedStateProvinces, setSelectedStateProvinces] = useState<
        string[]
    >([]);
    const [selectedStateProvince, setSelectedStateProvince] =
        useState<string>('');
    const [selectedDealership, setSelectedDealership] = useState<string[]>([]);
    const [dealershipsByState, setDealershipsByState] = useState<
        IDealerOption[]
    >([]);
    const [isLoadingRegions, setIsLoadingRegions] = useState<boolean>(false);
    const [isLoadingDistricts, setIsLoadingDistricts] =
        useState<boolean>(false);
    const [isLoadingDealerships, setIsLoadingDealerships] =
        useState<boolean>(false);
    const [ruleName, setRuleName] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false);
    const [notificationType, setNotificationType] = useState<NotificationType>(
        NotificationType.ERROR,
    );
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [saveSuccessMessage, setSaveSuccessMessage] = useState<string>('');
    const [isLoadingRuleDetails, setIsLoadingRuleDetails] = useState<boolean>(
        Boolean(distributionId),
    );
    const [pickerContext] = useState(() => {
        const state = routeState as {
            selectMode?: boolean;
            returnTo?: string;
            updateId?: string;
            formState?: unknown;
        } | null;

        return {
            isSelectMode: Boolean(state?.selectMode),
            returnTo:
                state?.returnTo ?? '/dashboard/systemadmin/upload-bulletin',
            updateId: state?.updateId,
            formState: state?.formState,
        };
    });
    const [isDeleteMenuOpen, setIsDeleteMenuOpen] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // Wait for the leave-page prompt to re-render with isSaved before navigating away.
    useEffect(() => {
        if (!isSaved || !saveSuccessMessage) return;

        if (pickerContext.isSelectMode) {
            const resolveSavedRuleId = distributionId
                ? Promise.resolve(distributionId)
                : new DistributionRuleService()
                      .getAllDistributionRules()
                      .then((response) => {
                          const savedRules: Array<{
                              distributionId: string;
                              ruleName: string;
                          }> = response.data ?? [];
                          return (
                              savedRules.find(
                                  (rule) => rule.ruleName === ruleName.trim(),
                              )?.distributionId ?? ''
                          );
                      })
                      .catch(() => '');

            void resolveSavedRuleId.then((savedRuleId) => {
                navigate(pickerContext.returnTo, {
                    state: {
                        selectedRule: {
                            distributionId: savedRuleId,
                            ruleName: ruleName.trim(),
                            country: selectedCountry,
                        },
                        ...(pickerContext.updateId && {
                            updateId: pickerContext.updateId,
                        }),
                        formState: pickerContext.formState,
                    },
                });
            });
            return;
        }

        navigate('/dashboard/systemadmin/view-distribution-rule', {
            state: { successMessage: saveSuccessMessage },
        });
    }, [isSaved, saveSuccessMessage, navigate]);

    const regionOptions = useMemo((): IDistributionMultiSelectOption[] => {
        return regions.map((region) => ({
            value: String(region.regionCode ?? region.orgUnitId),
            label: region.regionName,
        }));
    }, [regions]);

    const districtOptions = useMemo((): IDistributionMultiSelectOption[] => {
        return districts
            .filter((district) => !!district.districtName)
            .map((district) => ({
                value: String(district.districtId ?? district.districtName),
                label: district.districtName,
            }));
    }, [districts]);

    const countryOptions = useMemo((): IValueLabelOption[] => {
        return [
            {
                value: COUNTRIES.USA,
                label: systemAdmin.CREATE_RULE_PAGE_COUNTRY_OPTION_USA,
            },
            {
                value: COUNTRIES.CANADA,
                label: systemAdmin.CREATE_RULE_PAGE_COUNTRY_OPTION_CANADA,
            },
        ];
    }, [
        systemAdmin.CREATE_RULE_PAGE_COUNTRY_OPTION_USA,
        systemAdmin.CREATE_RULE_PAGE_COUNTRY_OPTION_CANADA,
    ]);

    const brandOptions = useMemo((): IValueLabelOption[] => {
        return [
            {
                value: 'Any',
                label: systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_ANY,
            },
            {
                value: BRANDS.VW,
                label: systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_VW,
            },
            {
                value: 'VWAndAudi',
                label: systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_VW_AND_AUDI,
            },
            {
                value: BRANDS.AUDI,
                label: systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_AUDI,
            },
            {
                value: BRANDS.DUCATI,
                label: systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_DUCATI,
            },
        ].sort((optionA, optionB) =>
            optionA.label.localeCompare(optionB.label),
        );
    }, [
        systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_VW,
        systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_VW_AND_AUDI,
        systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_ANY,
        systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_AUDI,
        systemAdmin.CREATE_RULE_PAGE_BRAND_OPTION_DUCATI,
    ]);

    const countryDropdownOptions = useMemo((): string[] => {
        return countryOptions.map((option) => option.label);
    }, [countryOptions]);

    const countryValueByDisplay = useMemo(() => {
        return countryOptions.reduce((lookup, option) => {
            lookup[option.label] = option.value;
            return lookup;
        }, {} as Record<string, string>);
    }, [countryOptions]);

    const countryDisplayByValue = useMemo(() => {
        return countryOptions.reduce((lookup, option) => {
            lookup[option.value] = option.label;
            return lookup;
        }, {} as Record<string, string>);
    }, [countryOptions]);

    const brandDropdownOptions = useMemo((): string[] => {
        return brandOptions.map((option) => option.label);
    }, [brandOptions]);

    const brandValueByDisplay = useMemo(() => {
        return brandOptions.reduce((lookup, option) => {
            lookup[option.label] = option.value;
            return lookup;
        }, {} as Record<string, string>);
    }, [brandOptions]);

    const brandDisplayByValue = useMemo(() => {
        return brandOptions.reduce((lookup, option) => {
            lookup[option.value] = option.label;
            return lookup;
        }, {} as Record<string, string>);
    }, [brandOptions]);

    const buildStateProvinceOptions = (
        country: string,
    ): IStateProvinceDropdownOption[] => {
        const isCanada = country === COUNTRIES.CANADA;
        const prefix = isCanada ? CANADA_PROVINCE_PREFIX : USA_STATE_PREFIX;

        return getStateOptions(isCanada, occupationIndividualInformation).map(
            (option: IStateProvinceOption) => ({
                code: option.value,
                value: `${prefix}${option.value}`,
                label: option.label,
            }),
        );
    };

    const stateProvinceOptions = useMemo((): IStateProvinceDropdownOption[] => {
        return buildStateProvinceOptions(selectedCountry);
    }, [selectedCountry, occupationIndividualInformation]);

    const stateProvinceDropdownOptions = useMemo((): string[] => {
        return stateProvinceOptions.map((option) => {
            if (option.label === option.code) {
                return option.code;
            }

            return `${option.code} - ${option.label}`;
        });
    }, [stateProvinceOptions]);

    const stateProvinceCodeByDisplay = useMemo(() => {
        return stateProvinceOptions.reduce((lookup, option) => {
            const displayValue =
                option.label === option.code
                    ? option.code
                    : `${option.code} - ${option.label}`;

            lookup[displayValue] = option.code;
            return lookup;
        }, {} as Record<string, string>);
    }, [stateProvinceOptions]);

    const stateProvinceValueByDisplay = useMemo(() => {
        return stateProvinceOptions.reduce((lookup, option) => {
            const displayValue =
                option.label === option.code
                    ? option.code
                    : `${option.code} - ${option.label}`;

            lookup[displayValue] = option.value;
            return lookup;
        }, {} as Record<string, string>);
    }, [stateProvinceOptions]);

    const dealershipOptions = useMemo((): IDistributionMultiSelectOption[] => {
        return dealershipsByState.map((dealer) => ({
            value: dealer.dealerId,
            label: getDealerDisplayLabel(dealer),
        }));
    }, [dealershipsByState]);

    const radioLabels = [
        systemAdmin.CREATE_RULE_PAGE_SCOPE_REGION_DISTRICT,
        systemAdmin.CREATE_RULE_PAGE_SCOPE_STATE_PROVINCE,
        systemAdmin.CREATE_RULE_PAGE_SCOPE_DEALERSHIP,
    ];
    const resetRegionDistrictSelections = () => {
        setSelectedRegions([]);
        setSelectedDistricts([]);
        setDistricts([]);
    };

    const resetStateDealershipSelections = () => {
        setSelectedStateProvinces([]);
        setSelectedStateProvince('');
        setSelectedDealership([]);
        setDealershipsByState([]);
    };

    const loadDealerIdsByState = async (
        state: string,
    ): Promise<IDealerOption[]> => {
        const service = new DealerManagementService();
        setIsLoadingDealerships(true);
        setErrorMessage('');
        setIsErrorVisible(false);

        try {
            const response = await service.getDealerIdsByState(state);
            const nextDealerships = normalizeDealersByState(response?.data);
            setDealershipsByState(nextDealerships);
            return nextDealerships;
        } catch {
            setDealershipsByState([]);
            setNotificationType(NotificationType.ERROR);
            setErrorMessage(systemAdmin.CREATE_RULE_PAGE_LOAD_DISTRICT_ERROR);
            setIsErrorVisible(true);
            return [];
        } finally {
            setIsLoadingDealerships(false);
        }
    };

    const loadActiveRegions = async (): Promise<IRegionOption[]> => {
        const service = new DealerManagementService();
        setIsLoadingRegions(true);
        setErrorMessage('');
        setIsErrorVisible(false);

        try {
            const response = await service.getAllActiveRegions();
            const nextRegions = normalizeRegions(response?.data);
            setRegions(nextRegions);
            return nextRegions;
        } catch {
            setRegions([]);
            setNotificationType(NotificationType.ERROR);
            setErrorMessage(systemAdmin.CREATE_RULE_PAGE_LOAD_REGION_ERROR);
            setIsErrorVisible(true);
            return [];
        } finally {
            setIsLoadingRegions(false);
        }
    };

    const loadDistrictsByRegion = async (
        regionOrgUnitIds: Array<string | number>,
    ) => {
        const service = new DealerManagementService();
        setIsLoadingDistricts(true);
        setErrorMessage('');
        setIsErrorVisible(false);

        try {
            const response = await service.getDistrictsByRegion(
                regionOrgUnitIds,
            );
            const nextDistricts = normalizeDistricts(response?.data);
            setDistricts(nextDistricts);
        } catch {
            setDistricts([]);
            setNotificationType(NotificationType.ERROR);
            setErrorMessage(systemAdmin.CREATE_RULE_PAGE_LOAD_DISTRICT_ERROR);
            setIsErrorVisible(true);
        } finally {
            setIsLoadingDistricts(false);
        }
    };

    const onScopeSelect = async (scope: RuleScope) => {
        if (scope === selectedScope) {
            return;
        }

        setSelectedScope(scope);
        setErrorMessage('');
        setIsErrorVisible(false);

        if (scope === RULE_SCOPES.REGION_DISTRICT) {
            resetStateDealershipSelections();
            resetRegionDistrictSelections();
            await loadActiveRegions();
            return;
        }

        if (scope === RULE_SCOPES.STATE_PROVINCE) {
            setRegions([]);
            resetRegionDistrictSelections();
            resetStateDealershipSelections();
            return;
        }

        if (scope === RULE_SCOPES.DEALERSHIP) {
            setRegions([]);
            resetRegionDistrictSelections();
            resetStateDealershipSelections();
            return;
        }

        setRegions([]);
        resetRegionDistrictSelections();
        resetStateDealershipSelections();
    };

    const onCountrySelect = (country: string) => {
        setSelectedCountry(countryValueByDisplay[country] ?? '');
        resetStateDealershipSelections();
    };

    const onBrandSelect = (brand: string) => {
        setSelectedBrand(brandValueByDisplay[brand] ?? '');
    };

    const onStateProvinceSelect = async (stateProvince: string) => {
        setSelectedStateProvince(stateProvince);
        setSelectedDealership([]);

        const stateCode =
            stateProvinceCodeByDisplay[stateProvince] ||
            stateProvince.split(' - ')[0];

        if (selectedScope !== RULE_SCOPES.DEALERSHIP) {
            setDealershipsByState([]);
            return;
        }

        await loadDealerIdsByState(stateCode);
    };

    const onStateProvincesSelect = (stateProvince: string | number) => {
        const selectedStateValue = String(stateProvince);
        const nextSelectedStateProvinces = selectedStateProvinces.includes(
            selectedStateValue,
        )
            ? selectedStateProvinces.filter(
                  (value) => value !== selectedStateValue,
              )
            : [...selectedStateProvinces, selectedStateValue];

        setSelectedStateProvinces(nextSelectedStateProvinces);
    };

    const onRegionSelect = async (regionValue: string | number) => {
        const selectedRegionValue = String(regionValue);
        const nextSelectedRegions = selectedRegions.includes(
            selectedRegionValue,
        )
            ? selectedRegions.filter((value) => value !== selectedRegionValue)
            : [...selectedRegions, selectedRegionValue];

        setSelectedRegions(nextSelectedRegions);
        setSelectedDistricts([]);

        if (!nextSelectedRegions.length) {
            setDistricts([]);
            return;
        }

        const regionOrgUnitIds = nextSelectedRegions
            .map((value) => {
                const option = regions.find(
                    (region) =>
                        String(region.regionCode ?? region.orgUnitId) === value,
                );
                return option?.orgUnitId;
            })
            .filter(
                (value): value is string | number =>
                    value !== undefined && value !== null && value !== '',
            );

        if (!regionOrgUnitIds.length) {
            setDistricts([]);
            return;
        }

        await loadDistrictsByRegion(regionOrgUnitIds);
    };

    const onDistrictSelect = (districtValue: string | number) => {
        const selectedDistrictValue = String(districtValue);
        const nextSelectedDistricts = selectedDistricts.includes(
            selectedDistrictValue,
        )
            ? selectedDistricts.filter(
                  (value) => value !== selectedDistrictValue,
              )
            : [...selectedDistricts, selectedDistrictValue];

        setSelectedDistricts(nextSelectedDistricts);
    };

    const onDealershipSelect = (dealerValue: string | number) => {
        const selectedDealerValue = String(dealerValue);
        const nextSelectedDealers = selectedDealership.includes(
            selectedDealerValue,
        )
            ? selectedDealership.filter(
                  (value) => value !== selectedDealerValue,
              )
            : [...selectedDealership, selectedDealerValue];

        setSelectedDealership(nextSelectedDealers);
    };

    const onSaveRule = async () => {
        const service = new DealerManagementService();
        const stateProvinceCode =
            stateProvinceValueByDisplay[selectedStateProvince] ||
            selectedStateProvince.split(' - ')[0];

        const payload: ISaveDistributionRulePayload = {
            name: ruleName.trim(),
            country: selectedCountry,
            brand: selectedBrand,
            states: [],
            regions: [],
            districts: [],
            dealers: [],
        };

        if (distributionId) {
            payload.key = distributionId;
        }

        if (selectedScope === RULE_SCOPES.REGION_DISTRICT) {
            payload.regions = selectedRegions.filter((regionValue) =>
                Boolean(regionValue),
            );

            payload.districts = selectedDistricts.filter((districtValue) =>
                Boolean(districtValue),
            );
        }

        if (selectedScope === RULE_SCOPES.STATE_PROVINCE) {
            payload.states = selectedStateProvinces.map(
                (stateProvince) =>
                    stateProvinceValueByDisplay[stateProvince] ||
                    stateProvince.split(' - ')[0],
            );
        }

        if (selectedScope === RULE_SCOPES.DEALERSHIP) {
            payload.states = selectedStateProvince ? [stateProvinceCode] : [];
            payload.dealers = selectedDealership.filter((dealerId) =>
                Boolean(dealerId),
            );
        }

        setErrorMessage('');
        setIsErrorVisible(false);
        setIsSaving(true);

        try {
            await service.saveDistributionRule(payload);
            const successMessage = distributionId
                ? systemAdmin.CREATE_RULE_PAGE_UPDATE_SUCCESS_MESSAGE
                : systemAdmin.CREATE_RULE_PAGE_SUCCESS_MESSAGE;

            setSaveSuccessMessage(successMessage);
            setIsSaved(true);
        } catch {
            setNotificationType(NotificationType.ERROR);
            setErrorMessage(errorMessages.API_FAILURE);
            setIsErrorVisible(true);
        } finally {
            setIsSaving(false);
        }
    };

    const onCancel = () => {
        if (pickerContext.isSelectMode) {
            navigate('/dashboard/systemadmin/view-distribution-rule', {
                state: {
                    selectMode: true,
                    returnTo: pickerContext.returnTo,
                    ...(pickerContext.updateId && {
                        updateId: pickerContext.updateId,
                    }),
                    formState: pickerContext.formState,
                },
            });
            return;
        }
        navigate('/dashboard/systemadmin/view-distribution-rule');
    };

    const handleDeleteMenuOpen = () => {
        setIsDeleteMenuOpen(false);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!distributionId) {
            return;
        }

        setErrorMessage('');
        setIsErrorVisible(false);
        setIsDeleting(true);

        new DistributionRuleService()
            .deleteDistributionRule(distributionId)
            .then((response) => {
                if (response?.status !== HttpStatusCode.SUCCESS) {
                    throw Error();
                }

                if (response.data?.deleted) {
                    navigate('/dashboard/systemadmin/view-distribution-rule', {
                        state: {
                            successMessage:
                                systemAdmin.DIST_RULE_DELETE_SUCCESS,
                        },
                    });
                    return;
                }

                setNotificationType(NotificationType.ERROR);
                setErrorMessage(systemAdmin.DIST_RULE_DELETE_IN_USE);
                setIsErrorVisible(true);
            })
            .catch(() => {
                setNotificationType(NotificationType.ERROR);
                setErrorMessage(systemAdmin.DIST_RULE_DELETE_ERROR);
                setIsErrorVisible(true);
            })
            .finally(() => {
                setIsDeleting(false);
                setIsDeleteModalOpen(false);
            });
    };

    const onBackHandler = () => {
        if (pickerContext.isSelectMode) {
            navigate('/dashboard/systemadmin/view-distribution-rule', {
                state: {
                    selectMode: true,
                    returnTo: pickerContext.returnTo,
                    ...(pickerContext.updateId && {
                        updateId: pickerContext.updateId,
                    }),
                    formState: pickerContext.formState,
                },
            });
            return;
        }

        if (distributionId) {
            navigate('/dashboard/systemadmin/view-distribution-rule');
            return;
        }

        navigate('/dashboard/systemadmin');
    };

    const prefillRuleDetails = async (details: IDistributionRuleDetails) => {
        const regionValues = toStringValues(details.regions);
        const districtValues = toStringValues(details.districts);
        const stateValues = toStringValues(details.states);
        const dealerValues = toStringValues(details.dealers);

        setRuleName(details.ruleName ?? '');
        setSelectedCountry(details.country ?? '');
        setSelectedBrand(details.brand ?? '');

        if (regionValues.length) {
            setSelectedScope(RULE_SCOPES.REGION_DISTRICT);

            const availableRegions = await loadActiveRegions();
            const regionOrgUnitIds = regionValues
                .map(
                    (regionValue) =>
                        availableRegions.find(
                            (region) =>
                                String(
                                    region.regionCode ?? region.orgUnitId,
                                ) === regionValue,
                        )?.orgUnitId,
                )
                .filter(
                    (value): value is string | number =>
                        value !== undefined && value !== null && value !== '',
                );

            setSelectedRegions(regionValues);

            if (regionOrgUnitIds.length) {
                await loadDistrictsByRegion(regionOrgUnitIds);
            }

            setSelectedDistricts(districtValues);
            return;
        }

        if (!stateValues.length) {
            return;
        }

        const availableStateOptions = buildStateProvinceOptions(
            details.country ?? '',
        );
        const stateDisplays = stateValues.map((stateValue) => {
            const stateCode = getStateProvinceCodeFromValue(stateValue);
            const stateOption = availableStateOptions.find(
                (option) => option.code === stateCode,
            );

            return stateOption
                ? getStateProvinceDisplayLabel(stateOption)
                : stateCode;
        });

        if (!dealerValues.length) {
            setSelectedStateProvinces(stateDisplays);
            setSelectedScope(RULE_SCOPES.STATE_PROVINCE);
            return;
        }

        const stateCode = getStateProvinceCodeFromValue(stateValues[0]);
        setSelectedStateProvince(stateDisplays[0]);
        setSelectedScope(RULE_SCOPES.DEALERSHIP);
        await loadDealerIdsByState(stateCode);
        setSelectedDealership(dealerValues);
    };

    const loadRuleDetails = async (ruleId: string) => {
        const service = new DistributionRuleService();
        setIsLoadingRuleDetails(true);

        try {
            const response = await service.getDistributionRuleDetails(ruleId);
            const details = response?.data as IDistributionRuleDetails;

            if (details) {
                await prefillRuleDetails(details);
            }

            setIsLoadingRuleDetails(false);
        } catch {
            setNotificationType(NotificationType.ERROR);
            setErrorMessage(
                systemAdmin.CREATE_RULE_PAGE_LOAD_RULE_DETAILS_ERROR,
            );
            setIsErrorVisible(true);
        }
    };

    useEffect(() => {
        if (distributionId) {
            void loadRuleDetails(distributionId);
            return;
        }

        void loadActiveRegions();
    }, []);

    const isSaveDisabled =
        !selectedCountry ||
        !selectedBrand ||
        (selectedScope === RULE_SCOPES.REGION_DISTRICT &&
            !selectedRegions.length) ||
        (selectedScope === RULE_SCOPES.STATE_PROVINCE &&
            !selectedStateProvinces.length) ||
        (selectedScope === RULE_SCOPES.DEALERSHIP &&
            (!selectedStateProvince || !selectedDealership.length)) ||
        !ruleName.trim() ||
        isSaving ||
        isLoadingRuleDetails;

    const hasUnsavedChanges = useMemo(() => {
        return (
            Boolean(ruleName.trim()) ||
            Boolean(selectedCountry) ||
            Boolean(selectedBrand) ||
            selectedScope !== RULE_SCOPES.REGION_DISTRICT ||
            selectedRegions.length > EMPTY_OPTIONS ||
            selectedDistricts.length > EMPTY_OPTIONS ||
            selectedStateProvinces.length > EMPTY_OPTIONS ||
            Boolean(selectedStateProvince) ||
            selectedDealership.length > EMPTY_OPTIONS
        );
    }, [
        ruleName,
        selectedCountry,
        selectedBrand,
        selectedScope,
        selectedRegions,
        selectedDistricts,
        selectedStateProvinces,
        selectedStateProvince,
        selectedDealership,
    ]);

    const getSelectedSummaryText = (count: number) => {
        return `${count} ${systemAdmin.CREATE_RULE_PAGE_SELECTED_COUNT_SUFFIX}`;
    };

    const leavePagePrompt = {
        TITLE: 'Are you sure you want to leave?',
        DESCRIPTION: 'Any information youve entered will not be saved.',
        CANCEL_BTN_TXT: 'Cancel',
        PROCEED_BRN_TXT: 'Yes, confirm',
    };

    return (
        <div className='create-distribution-rule'>
            <Prompt
                show={hasUnsavedChanges && !isSaved && !isDeleting}
                beforeUnload={true}
                titleContent={leavePagePrompt.TITLE}
                bodyContent={leavePagePrompt.DESCRIPTION}
                cancelBtnLabel={leavePagePrompt.CANCEL_BTN_TXT}
                confirmBtnLabel={leavePagePrompt.PROCEED_BRN_TXT}
                onCancelHandler={() => {
                    // Intentionally empty.
                }}
                onConfirmHandler={() => {
                    // Intentionally empty.
                }}
                isContentCentered={false}
                showButtons={true}
                icon={''}
                isBigModal={false}
                isError={false}
                progress={undefined}
            />
            <div
                className={`create-distribution-rule__nav-header ${brand.toLowerCase()}`}
            >
                <div className='one'>
                    <button
                        className='c-btn c-btn--link c-btn--small c-btn--simple'
                        type='button'
                        onClick={onBackHandler}
                    >
                        <i
                            className='c-btn__icon c-icon c-icon--[semantic-back]'
                            aria-hidden='true'
                            role='img'
                        ></i>
                        <span className='c-btn__text'>
                            {systemAdmin.CREATE_RULE_PAGE_BACK_BUTTON}
                        </span>
                    </button>
                </div>
            </div>

            {isErrorVisible && errorMessage && (
                <div className='create-distribution-rule__notification'>
                    <Notification
                        content={errorMessage}
                        type={notificationType}
                        visibility={isErrorVisible}
                        setVisibility={setIsErrorVisible}
                    />
                </div>
            )}

            <div className='create-distribution-rule__content-wrapper'>
                <div className='create-distribution-rule__card'>
                    <section className='c-form-section'>
                        {isLoadingRuleDetails ? (
                            <RuleFormSkeleton />
                        ) : (
                            <div className='c-form-section__content create-distribution-rule__content'>
                                <div className='page-title'>
                                    <h5>
                                        {distributionId
                                            ? systemAdmin.EDIT_RULE_PAGE_TITLE
                                            : systemAdmin.CREATE_RULE_PAGE_TITLE}
                                    </h5>
                                </div>
                                <p className='create-distribution-rule__description'>
                                    {systemAdmin.CREATE_RULE_PAGE_DESCRIPTION}
                                </p>

                                <div className='c-form-field mb-large create-distribution-rule__rule-name-field'>
                                    <label
                                        className='c-form-field__label'
                                        htmlFor='distribution-rule-name'
                                    >
                                        {
                                            systemAdmin.CREATE_RULE_PAGE_RULE_NAME_LABEL
                                        }
                                        <span> *</span>
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                id='distribution-rule-name'
                                                className='c-input__input'
                                                type='text'
                                                name='ruleName'
                                                maxLength={RULE_NAME_MAX_LENGTH}
                                                placeholder={
                                                    systemAdmin.CREATE_RULE_PAGE_RULE_NAME_PLACEHOLDER
                                                }
                                                value={ruleName}
                                                onChange={(event) => {
                                                    setRuleName(
                                                        event.target.value,
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {distributionId && (
                                        <div className='create-distribution-rule__rule-actions'>
                                            <button
                                                className='options-icon'
                                                type='button'
                                                data-testid='edit-rule-options-btn'
                                                aria-label={
                                                    systemAdmin.DIST_RULE_MENU_DELETE
                                                }
                                                aria-expanded={isDeleteMenuOpen}
                                                onClick={() =>
                                                    setIsDeleteMenuOpen(true)
                                                }
                                            >
                                                <ProfileOptionsIcon />
                                            </button>
                                            <Dropdown
                                                dropdown={isDeleteMenuOpen}
                                                showSelected={false}
                                                showSelectedIcon={false}
                                                handlerChange={(
                                                    value: boolean,
                                                ) => {
                                                    setIsDeleteMenuOpen(value);
                                                }}
                                            >
                                                <li
                                                    className='menu-items'
                                                    data-testid='edit-rule-menu-delete'
                                                    onClick={
                                                        handleDeleteMenuOpen
                                                    }
                                                >
                                                    <span className='delete'>
                                                        {
                                                            systemAdmin.DIST_RULE_MENU_DELETE
                                                        }
                                                    </span>
                                                </li>
                                            </Dropdown>
                                        </div>
                                    )}
                                </div>

                                <div className='create-distribution-rule__top-select-row mb-large'>
                                    <div className='create-distribution-rule__selector-field'>
                                        <label className='create-distribution-rule__selector-label'>
                                            {
                                                systemAdmin.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL
                                            }
                                            <span> *</span>
                                        </label>
                                        <ComboBox
                                            name='distribution-rule-country'
                                            customClass='create-distribution-rule__combobox'
                                            options={countryDropdownOptions}
                                            placeholder={
                                                systemAdmin.CREATE_RULE_PAGE_COUNTRY_PLACEHOLDER
                                            }
                                            selectedOption={
                                                countryDisplayByValue[
                                                    selectedCountry
                                                ] ?? ''
                                            }
                                            onSelect={onCountrySelect}
                                        />
                                    </div>

                                    <div className='create-distribution-rule__selector-field'>
                                        <label className='create-distribution-rule__selector-label'>
                                            {
                                                systemAdmin.SYSTEM_ADMIN_PAGE_BRAND_LABEL
                                            }
                                            <span> *</span>
                                        </label>
                                        <ComboBox
                                            name='distribution-rule-brand'
                                            customClass='create-distribution-rule__combobox'
                                            options={brandDropdownOptions}
                                            placeholder={
                                                systemAdmin.CREATE_RULE_PAGE_BRAND_PLACEHOLDER
                                            }
                                            selectedOption={
                                                brandDisplayByValue[
                                                    selectedBrand
                                                ] ?? ''
                                            }
                                            onSelect={onBrandSelect}
                                        />
                                    </div>
                                </div>

                                <div className='create-distribution-rule__section-title'>
                                    {
                                        systemAdmin.CREATE_RULE_PAGE_RULE_TYPE_TITLE
                                    }
                                </div>
                                <p className='create-distribution-rule__section-subtitle'>
                                    {
                                        systemAdmin.CREATE_RULE_PAGE_RULE_TYPE_DESCRIPTION
                                    }
                                </p>

                                <div className='create-distribution-rule__type-container'>
                                    <div className='create-distribution-rule__type-options'>
                                        <RadioButton
                                            name='distribution-rule-scope'
                                            options={RADIO_OPTIONS}
                                            labels={radioLabels}
                                            selectedOption={selectedScope}
                                            onSelect={onScopeSelect}
                                        />
                                    </div>

                                    <div className='create-distribution-rule__divider' />

                                    <div className='create-distribution-rule__selectors'>
                                        {selectedScope ===
                                            RULE_SCOPES.REGION_DISTRICT && (
                                            <>
                                                <div className='create-distribution-rule__selector-field'>
                                                    <label
                                                        className='create-distribution-rule__selector-label'
                                                        htmlFor='distribution-rule-region'
                                                    >
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_REGION_LABEL
                                                        }
                                                        <span> *</span>
                                                        {selectedRegions.length >
                                                            SINGLE_SELECTION && (
                                                            <span className='create-distribution-rule__selector-count'>
                                                                <strong>
                                                                    {' - '}
                                                                    {getSelectedSummaryText(
                                                                        selectedRegions.length,
                                                                    )}
                                                                </strong>
                                                            </span>
                                                        )}
                                                    </label>
                                                    <DistributionMultiSelectComboBox
                                                        name='distribution-rule-region'
                                                        customClass='create-distribution-rule__combobox'
                                                        options={regionOptions}
                                                        placeholder={
                                                            systemAdmin.CREATE_RULE_PAGE_REGION_PLACEHOLDER
                                                        }
                                                        selectedOptions={
                                                            selectedRegions
                                                        }
                                                        multipleValuesSelectedLabel={
                                                            systemAdmin.CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED
                                                        }
                                                        onSelect={
                                                            onRegionSelect
                                                        }
                                                        disabled={
                                                            isLoadingRegions
                                                        }
                                                    />
                                                    <p className='create-distribution-rule__hint'>
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_REGION_HELP_TEXT
                                                        }
                                                    </p>
                                                </div>

                                                <div className='create-distribution-rule__selector-field'>
                                                    <label
                                                        className='create-distribution-rule__selector-label'
                                                        htmlFor='distribution-rule-district'
                                                    >
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_DISTRICT_LABEL
                                                        }
                                                        {selectedDistricts.length >
                                                            SINGLE_SELECTION && (
                                                            <span className='create-distribution-rule__selector-count'>
                                                                <strong>
                                                                    {' - '}
                                                                    {getSelectedSummaryText(
                                                                        selectedDistricts.length,
                                                                    )}
                                                                </strong>
                                                            </span>
                                                        )}
                                                    </label>
                                                    {isLoadingDistricts ? (
                                                        <div
                                                            className='create-distribution-rule__dropdown-skeleton'
                                                            role='status'
                                                            aria-busy='true'
                                                        />
                                                    ) : (
                                                        <DistributionMultiSelectComboBox
                                                            name='distribution-rule-district'
                                                            customClass='create-distribution-rule__combobox'
                                                            options={
                                                                districtOptions
                                                            }
                                                            placeholder={
                                                                systemAdmin.CREATE_RULE_PAGE_DISTRICT_PLACEHOLDER
                                                            }
                                                            selectedOptions={
                                                                selectedDistricts
                                                            }
                                                            multipleValuesSelectedLabel={
                                                                systemAdmin.CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED
                                                            }
                                                            onSelect={
                                                                onDistrictSelect
                                                            }
                                                            disabled={
                                                                !selectedRegions.length ||
                                                                districtOptions.length ===
                                                                    EMPTY_OPTIONS
                                                            }
                                                        />
                                                    )}
                                                    <p className='create-distribution-rule__hint'>
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_DISTRICT_HELP_TEXT
                                                        }
                                                    </p>
                                                </div>
                                            </>
                                        )}

                                        {selectedScope ===
                                            RULE_SCOPES.STATE_PROVINCE && (
                                            <div className='create-distribution-rule__selector-field'>
                                                <label
                                                    className='create-distribution-rule__selector-label'
                                                    htmlFor='distribution-rule-state-province'
                                                >
                                                    {
                                                        systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_LABEL
                                                    }
                                                    <span> *</span>
                                                </label>
                                                <DistributionMultiSelectComboBox
                                                    name='distribution-rule-state-province'
                                                    customClass='create-distribution-rule__combobox'
                                                    options={
                                                        stateProvinceDropdownOptions
                                                    }
                                                    placeholder={
                                                        systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_PLACEHOLDER
                                                    }
                                                    selectedOptions={
                                                        selectedStateProvinces
                                                    }
                                                    multipleValuesSelectedLabel={
                                                        systemAdmin.CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED
                                                    }
                                                    onSelect={
                                                        onStateProvincesSelect
                                                    }
                                                    disabled={!selectedCountry}
                                                />
                                                <p className='create-distribution-rule__hint'>
                                                    {
                                                        systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_HELP_TEXT
                                                    }
                                                </p>
                                            </div>
                                        )}

                                        {selectedScope ===
                                            RULE_SCOPES.DEALERSHIP && (
                                            <>
                                                <div className='create-distribution-rule__selector-field'>
                                                    <label
                                                        className='create-distribution-rule__selector-label'
                                                        htmlFor='distribution-rule-state-province-dealer'
                                                    >
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_LABEL
                                                        }
                                                        <span> *</span>
                                                    </label>
                                                    <ComboBox
                                                        name='distribution-rule-state-province-dealer'
                                                        customClass='create-distribution-rule__combobox'
                                                        options={
                                                            stateProvinceDropdownOptions
                                                        }
                                                        placeholder={
                                                            systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_PLACEHOLDER
                                                        }
                                                        selectedOption={
                                                            selectedStateProvince
                                                        }
                                                        onSelect={
                                                            onStateProvinceSelect
                                                        }
                                                        disabled={
                                                            !selectedCountry
                                                        }
                                                    />
                                                    <p className='create-distribution-rule__hint'>
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_STATE_PROVINCE_HELP_TEXT
                                                        }
                                                    </p>
                                                </div>

                                                <div className='create-distribution-rule__selector-field'>
                                                    <label
                                                        className='create-distribution-rule__selector-label'
                                                        htmlFor='distribution-rule-dealership'
                                                    >
                                                        {
                                                            systemAdmin.CREATE_RULE_PAGE_DEALERSHIP_LABEL
                                                        }
                                                        <span> *</span>
                                                        {selectedDealership.length >
                                                            SINGLE_SELECTION && (
                                                            <span className='create-distribution-rule__selector-count'>
                                                                <strong>
                                                                    {' - '}
                                                                    {getSelectedSummaryText(
                                                                        selectedDealership.length,
                                                                    )}
                                                                </strong>
                                                            </span>
                                                        )}
                                                    </label>
                                                    {isLoadingDealerships ? (
                                                        <div
                                                            className='create-distribution-rule__dropdown-skeleton'
                                                            role='status'
                                                            aria-busy='true'
                                                        />
                                                    ) : (
                                                        <DistributionMultiSelectComboBox
                                                            name='distribution-rule-dealership'
                                                            customClass='create-distribution-rule__combobox'
                                                            options={
                                                                dealershipOptions
                                                            }
                                                            placeholder={
                                                                systemAdmin.CREATE_RULE_PAGE_DEALERSHIP_PLACEHOLDER
                                                            }
                                                            selectedOptions={
                                                                selectedDealership
                                                            }
                                                            multipleValuesSelectedLabel={
                                                                systemAdmin.CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED
                                                            }
                                                            onSelect={
                                                                onDealershipSelect
                                                            }
                                                            disabled={
                                                                !selectedStateProvince ||
                                                                dealershipOptions.length ===
                                                                    EMPTY_OPTIONS
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className='create-distribution-rule__actions'>
                                    <button
                                        className='c-btn c-btn--secondary'
                                        type='button'
                                        onClick={onCancel}
                                    >
                                        <span className='c-btn__text'>
                                            {
                                                systemAdmin.CREATE_RULE_PAGE_CANCEL_BUTTON
                                            }
                                        </span>
                                    </button>
                                    <button
                                        className='c-btn'
                                        type='button'
                                        disabled={isSaveDisabled}
                                        onClick={onSaveRule}
                                    >
                                        <span className='c-btn__text'>
                                            {
                                                systemAdmin.CREATE_RULE_PAGE_SAVE_BUTTON
                                            }
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
            <div className='create-dist-rule-privacy-footer'>
                <FooterPrivacy />
            </div>
            <DialogModal
                titleContent={systemAdmin.DIST_RULE_DELETE_MODAL_TITLE}
                bodyContent={systemAdmin.DIST_RULE_DELETE_MODAL_BODY}
                open={isDeleteModalOpen}
                cancelBtnLabel={systemAdmin.DIST_RULE_DELETE_MODAL_CANCEL}
                confirmBtnLabel={systemAdmin.DIST_RULE_DELETE_MODAL_CONFIRM}
                onCancelHandler={() => setIsDeleteModalOpen(false)}
                onConfirmHandler={handleDeleteConfirm}
                isLoading={isDeleting}
                isConfirmBtnRed
                keepOpenOnConfirm
            />
        </div>
    );
};

export default CreateDistributionRule;
